import type { ColumnDef } from "@tanstack/react-table";
import type { Table } from "@tanstack/table-core";
import { Checkbox } from "monday-ui-react-core";
import React from "react";

import type { MondayBoardColumnType } from "@/services/monday-service";

import type { Item } from "../../types/common";
import type { TableMeta } from "./table";
import { GroupsColor, MemoizedItem } from "./table";

const MemoizedGroup = React.memo(
  function GroupCell({ color }: { color?: string }) {
    return (
      <GroupsColor>
        <div className="color" style={{ background: color }} />
      </GroupsColor>
    );
  },
  function (prev, next) {
    return prev.color === next.color;
  }
);

export const ITEM_DEPTH = 0;
export const SUBITEM_DEPTH = 1;

export interface ColumnMeta {
  depth?: number[];
  columnType?: MondayBoardColumnType;
  sticky?: boolean;
  isAsyncPersonalPhase?: boolean;
}

export const MemoizedCheckbox = React.memo(
  function Check({
    disabled,
    checked,
    onChange,
  }: {
    disabled: boolean;
    checked: boolean;
    onChange: (event: unknown) => void;
  }) {
    return <Checkbox disabled={disabled} checked={checked} onChange={onChange} />;
  },
  function (prev, next) {
    return prev.checked === next.checked && prev.disabled === next.disabled;
  }
);

export function generateItemColumns(): ColumnDef<Item>[] {
  return [
    {
      id: "color",
      size: 6,
      minSize: 6,
      meta: { depth: [ITEM_DEPTH, SUBITEM_DEPTH], sticky: true },
      header: ({ table }) => <MemoizedGroup color={(table.options.meta as TableMeta)?.color} />,
      cell: ({ table }) => <MemoizedGroup color={(table.options.meta as TableMeta)?.color} />,
    },
    {
      id: "select",
      header: ({ table }) => {
        const allRows = table.getRowModel().flatRows;
        const selectedRows = table.getSelectedRowModel().flatRows;
        const depth = (table.options.meta as TableMeta)?.depth;
        const selectedItemsIds = allRows.filter((r) => r.depth === (depth || ITEM_DEPTH)).map((r) => r.id);
        const ids = selectedRows.map((r) => r.id);
        const isAllSelected = selectedItemsIds.length > 0 && selectedItemsIds.every((id) => ids.includes(id));
        const indeterminate = !isAllSelected && selectedRows.length > 0;
        const onToggleAllRowsHandler = (table: Table<Item>) => {
          if (!isAllSelected) {
            const selection = selectedItemsIds.reduce<{ [key: string]: boolean }>((ids, key) => {
              ids[key] = true;
              return ids;
            }, {});
            table.setRowSelection(selection);
            if (depth === SUBITEM_DEPTH) {
              table.toggleAllRowsExpanded();
            }
          } else {
            table.setRowSelection({});
          }
        };

        return (
          <Checkbox
            indeterminate={indeterminate}
            checked={isAllSelected}
            onChange={() => onToggleAllRowsHandler(table)}
          />
        );
      },
      size: 45,
      meta: { depth: [ITEM_DEPTH, SUBITEM_DEPTH], sticky: true },
      cell: ({ row }) => (
        <MemoizedCheckbox
          disabled={!row.getCanSelect()}
          checked={row.getIsSelected()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
    },
    {
      id: "item",
      header: "Item",
      accessorKey: "name",
      size: 500,
      meta: { depth: [ITEM_DEPTH], sticky: true },
      cell: ({ cell }) => {
        const isExpanded = cell.row.getIsExpanded();
        return <MemoizedItem cell={cell} isExpanded={isExpanded} />;
      },
    },
    {
      id: "subitem",
      header: "Subitem",
      accessorKey: "name",
      size: 500,
      meta: { depth: [SUBITEM_DEPTH], sticky: true },
      cell: ({ cell }) => {
        const isExpanded = cell.row.getIsExpanded();
        return <MemoizedItem cell={cell} isExpanded={isExpanded} />;
      },
    },
  ];
}
